import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { IoCalendarOutline, IoFlagOutline } from "react-icons/io5";
import EAButton from "../../shared/Buttons/EAButton";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";
import EADayCalendar from "../../shared/EADayCalendar/EADayCalendar";

interface IProps {
  startDate: Date;
  endDate: Date;
  minDate: Date;
  maxDate: Date;
  canChangeDate?: boolean;
  onStartDateChange: (date: Date) => void;
  onCalendarOpenChange?: (isOpen: boolean) => void;
}

export const PassDateBars: React.FC<IProps> = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  canChangeDate = true,
  onStartDateChange,
  onCalendarOpenChange,
}) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarHeight, setCalendarHeight] = useState(0);
  const calendarContentRef = useRef<HTMLDivElement>(null);

  const formatDay = (date: Date) => format(date, "d MMMM yyyy", { locale });

  useEffect(() => {
    onCalendarOpenChange?.(isCalendarOpen);
  }, [isCalendarOpen, onCalendarOpenChange]);

  useLayoutEffect(() => {
    const content = calendarContentRef.current;
    if (!content) return;
    setCalendarHeight(isCalendarOpen ? content.scrollHeight : 0);
  }, [isCalendarOpen, startDate, endDate, locale]);

  const handleSelect = (date?: Date) => {
    if (!date) return;
    onStartDateChange(date);
    setIsCalendarOpen(false);
  };

  return (
    <div className="pass-date-bars">
      <div className="pass-date-bar">
        <div className="pass-date-bar__main">
          <IoCalendarOutline className="pass-date-bar__icon" aria-hidden="true" />
          <div className="pass-date-bar__text">
            <span className="body-text-small text-color-dark-gray">{t("data_rozpoczecia")}</span>
            <p className="body-text-large text-bold">{formatDay(startDate)}</p>
          </div>
        </div>
        {canChangeDate && (
          <div className="pass-date-bar__toggle">
            <EAButton
              variant="filled"
              compact
              title={t("zmien")}
              onClick={() => setIsCalendarOpen((open) => !open)}
            />
          </div>
        )}
      </div>

      {canChangeDate && (
        <div
          className={`pass-date-bars__calendar${isCalendarOpen ? " is-open" : ""}`}
          style={{ height: calendarHeight }}
          aria-hidden={!isCalendarOpen}
        >
          <div ref={calendarContentRef}>
            <EADayCalendar
              selectedDate={startDate}
              fromDate={minDate}
              toDate={maxDate}
              onSelect={handleSelect}
            />
          </div>
        </div>
      )}

      <div className="pass-date-bar">
        <IoFlagOutline className="pass-date-bar__icon" aria-hidden="true" />
        <div className="pass-date-bar__text">
          <span className="body-text-small text-color-dark-gray">{t("data_zakonczenia")}</span>
          <p className="body-text-large text-bold">{formatDay(endDate)}</p>
        </div>
      </div>
    </div>
  );
};

export default PassDateBars;
